import * as XLSX from 'xlsx';

export type ServiceStatus =
  | 'confirmed'
  | 'assigned'
  | 'in_transit'
  | 'arrived'
  | 'in_progress'
  | 'completed'
  | 'cancelled';

export type PaymentStatus =
  | 'Pending Collection'
  | 'Collected via Cash'
  | 'Collected via UPI';

export type BookingSource = 'web' | 'call' | 'instagram' | 'whatsapp' | 'walkin' | 'other';

export interface BookingRecord {
  bookingRef: string;
  timestamp: string;
  clientName: string;
  phone: string;
  area: string;
  address: string;
  pincode?: string;
  preferredDate: string;
  preferredSlot: string;
  specialRequests?: string;
  items: Array<{
    id: string;
    name: string;
    category?: string;
    price: number;
    originalPrice?: number;
    durationMinutes?: number;
  }>;
  couponCode?: string;
  couponDiscount?: number;
  servicePerson?: string;
  status?: string;
  totalPrice: number;
  totalOriginalPrice: number;
  totalSavings: number;

  // New Production Fields for 3-Sided System & Pay After Service
  startOtp: string; // 4-digit security code for doorstep service start
  paymentMethod?: string; // Always 'Pay After Service'
  paymentStatus?: PaymentStatus;
  partnerId?: string;
  partnerName?: string;
  partnerPhone?: string;
  serviceStatus?: ServiceStatus;
  startedAt?: string;
  completedAt?: string;
  customerRating?: number; // 1 to 5
  customerReview?: string;
  bookingSource?: BookingSource;

  // Hyperlocal Location Tracking & Google Maps Navigation
  googleMapsUrl?: string;
  latitude?: number;
  longitude?: number;
  distanceKm?: number;
}

const STORAGE_KEY = 'blishka_bookings_log';

/**
 * Saves a new booking or updates an existing booking in localStorage master log
 */
export function saveBookingToStorage(booking: BookingRecord): void {
  try {
    const existing = getStoredBookings();
    const index = existing.findIndex((b) => b.bookingRef === booking.bookingRef);
    if (index >= 0) {
      existing[index] = { ...existing[index], ...booking };
    } else {
      existing.unshift(booking);
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
  } catch (err) {
    console.error('Failed to save booking to localStorage:', err);
  }
}

/**
 * Updates status or specific fields of a stored booking
 */
export function updateStoredBooking(
  bookingRef: string,
  updates: Partial<BookingRecord>
): BookingRecord | null {
  try {
    const existing = getStoredBookings();
    const index = existing.findIndex((b) => b.bookingRef === bookingRef);
    if (index >= 0) {
      existing[index] = { ...existing[index], ...updates };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
      return existing[index];
    }
    return null;
  } catch (err) {
    console.error('Failed to update booking in localStorage:', err);
    return null;
  }
}

/**
 * Deletes a booking by reference ID
 */
export function deleteStoredBooking(bookingRef: string): void {
  try {
    const existing = getStoredBookings();
    const updated = existing.filter((b) => b.bookingRef !== bookingRef);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (err) {
    console.error('Failed to delete booking from localStorage:', err);
  }
}

/**
 * Retrieves all stored bookings
 */
export function getStoredBookings(): BookingRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * Generates and downloads a beautifully formatted Excel workbook (.xlsx)
 * for an individual booking order.
 */
export function exportBookingToExcel(booking: BookingRecord): void {
  const wb = XLSX.utils.book_new();

  // Create a structured data array for the sheet
  const rows: (string | number)[][] = [
    ['BLISHKA DOORSTEP SALON & PARLOUR'],
    ['Doorstep Beauty & Pampering Services · Bhilai & Durg'],
    ['Website: blishka.in | Support WhatsApp: +91 88153 66617'],
    [],
    ['═════════════════════════════════════════════════════════════════════════════'],
    ['APPOINTMENT BOOKING ORDER DETAILS'],
    ['═════════════════════════════════════════════════════════════════════════════'],
    ['Booking Reference ID', booking.bookingRef],
    ['Order Placed On', booking.timestamp],
    ['Booking Source / Channel', (booking.bookingSource || 'web').toUpperCase()],
    ['Service Start OTP', booking.startOtp || 'N/A'],
    ['Booking Status', (booking.serviceStatus || booking.status || 'CONFIRMED').toUpperCase()],
    ['Assigned Beautician', booking.servicePerson || booking.partnerName || 'To be assigned'],
    [],
    ['─── CUSTOMER & ADDRESS DETAILS ─────────────────────────────────────────────'],
    ['Customer Full Name', booking.clientName],
    ['Contact / WhatsApp No.', booking.phone],
    ['Service Area / Zone', booking.area],
    ['Complete Home Address', booking.address],
    ['Pincode / Landmark', booking.pincode || 'Not Specified'],
    [],
    ['─── APPOINTMENT SCHEDULE ───────────────────────────────────────────────────'],
    ['Preferred Service Date', booking.preferredDate || 'Flexible / As soon as possible'],
    ['Preferred Time Slot', booking.preferredSlot],
    ['Special Requests / Notes', booking.specialRequests || 'None'],
    [],
    ['─── SELECTED SERVICES & PACKAGES ───────────────────────────────────────────'],
    ['S.No.', 'Service / Package Name', 'Category', 'Salon Rate (₹)', 'Blishka Rate (₹)', 'You Saved (₹)']
  ];

  // Append items
  booking.items.forEach((item, index) => {
    const salonRate = item.originalPrice || Math.round(item.price * 1.35);
    const savings = salonRate - item.price;
    rows.push([
      index + 1,
      item.name,
      item.category ? item.category.toUpperCase() : 'SALON CARE',
      salonRate,
      item.price,
      savings
    ]);
  });

  // Financial summary
  rows.push(
    [],
    ['─── PAYMENT & ORDER SUMMARY ────────────────────────────────────────────────'],
    ['Total Standard Salon Value', `₹${booking.totalOriginalPrice.toLocaleString('en-IN')}`],
    ['Total Blishka Discounted Price', `₹${(booking.totalPrice + (booking.couponDiscount || 0)).toLocaleString('en-IN')}`],
    ...(booking.couponCode ? [['Applied Coupon Code', `${booking.couponCode} (-₹${booking.couponDiscount || 0})`]] : []),
    ['Final Amount Payable at Home', `₹${booking.totalPrice.toLocaleString('en-IN')}`],
    ['TOTAL NET SAVINGS', `₹${(booking.totalSavings + (booking.couponDiscount || 0)).toLocaleString('en-IN')}`],
    ['Payment Mode', booking.paymentMethod || 'Pay After Service (Cash / Direct UPI)'],
    ['Payment Collection Status', booking.paymentStatus || 'Pending Collection'],
    ['Customer Rating', booking.customerRating ? `${booking.customerRating} / 5 Stars` : 'Pending review'],
    ['Customer Feedback', booking.customerReview || 'None'],
    [],
    ['═════════════════════════════════════════════════════════════════════════════'],
    ['SERVICE GUARANTEE: 100% Single-use Disposables · Verified Expert Beautician'],
    ['Thank you for choosing Blishka! blissfully you!! ♡']
  );

  // Convert to worksheet
  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Set nice column widths
  ws['!cols'] = [
    { wch: 28 }, // Col A
    { wch: 38 }, // Col B
    { wch: 18 }, // Col C
    { wch: 16 }, // Col D
    { wch: 18 }, // Col E
    { wch: 16 }  // Col F
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Booking_Details');

  // Trigger download
  const fileName = `Blishka_Booking_${booking.bookingRef}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

/**
 * Exports all historical bookings into a master spreadsheet
 */
export function exportMasterBookingsToExcel(customList?: BookingRecord[]): void {
  const allBookings = customList && customList.length > 0 ? customList : getStoredBookings();
  if (allBookings.length === 0) {
    alert('No bookings found to export.');
    return;
  }

  const wb = XLSX.utils.book_new();

  const masterRows: (string | number)[][] = [
    ['BLISHKA - MASTER APPOINTMENTS REGISTER'],
    [`Exported On: ${new Date().toLocaleString('en-IN')}`],
    [],
    [
      'Booking Ref',
      'Start OTP',
      'Channel / Source',
      'Booking Date',
      'Customer Name',
      'Phone',
      'Area',
      'Address',
      'Appointment Date',
      'Time Slot',
      'Services Ordered',
      'Coupon Used',
      'Total Amount (₹)',
      'Total Savings (₹)',
      'Service Status',
      'Assigned Beautician',
      'Payment Mode',
      'Payment Status',
      'Rating',
      'Customer Review',
      'Special Notes'
    ]
  ];

  allBookings.forEach((b) => {
    masterRows.push([
      b.bookingRef,
      b.startOtp || 'N/A',
      (b.bookingSource || 'web').toUpperCase(),
      b.timestamp,
      b.clientName,
      b.phone,
      b.area,
      b.address,
      b.preferredDate || 'Flexible',
      b.preferredSlot,
      b.items.map((i) => i.name).join(', '),
      b.couponCode ? `${b.couponCode} (-₹${b.couponDiscount || 0})` : 'None',
      b.totalPrice,
      b.totalSavings,
      b.serviceStatus || b.status || 'Confirmed',
      b.servicePerson || b.partnerName || 'To be assigned',
      b.paymentMethod || 'Pay After Service',
      b.paymentStatus || 'Pending Collection',
      b.customerRating ? `${b.customerRating}★` : 'None',
      b.customerReview || '-',
      b.specialRequests || '-'
    ]);
  });

  const ws = XLSX.utils.aoa_to_sheet(masterRows);
  ws['!cols'] = [
    { wch: 18 },
    { wch: 12 },
    { wch: 20 },
    { wch: 22 },
    { wch: 15 },
    { wch: 24 },
    { wch: 35 },
    { wch: 16 },
    { wch: 22 },
    { wch: 40 },
    { wch: 16 },
    { wch: 16 },
    { wch: 16 },
    { wch: 18 },
    { wch: 28 },
    { wch: 20 },
    { wch: 22 },
    { wch: 10 },
    { wch: 30 },
    { wch: 30 }
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'All_Bookings');
  XLSX.writeFile(wb, `Blishka_Master_Bookings_${new Date().toISOString().split('T')[0]}.xlsx`);
}
